import { fromMicroUsd, type MicroUsd } from "@charisol-spark/billing";

/**
 * The two formatters the admin pages need, copied from
 * packages/frontend/src/utils/formatting.ts (the rest of that file —
 * formatDate/formatNumber/formatPercent — is used only by user-app features
 * that stayed in packages/frontend, so wasn't duplicated here).
 */

/** Below this, a cost shows as "<$0.0001" rather than rounding to zero. */
const SMALLEST_SHOWN_USD = 0.0001;

/**
 * Format a cost recorded in micro-dollars. Provider costs are fractions of a
 * cent per call, so two decimals would show most per-user figures as
 * "$0.00" — this keeps up to four decimals under a dollar, and says so
 * instead of rounding to zero below that.
 */
export function formatMicroUsd(microUsd: MicroUsd): string {
  const usd = fromMicroUsd(microUsd);
  if (usd > 0 && usd < SMALLEST_SHOWN_USD) return "<$0.0001";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.abs(usd) < 1 ? 4 : 2,
  }).format(usd);
}

/**
 * A billing period (`YYYY-MM`, UTC) as a month name: "October 2026".
 * Returns the input unchanged if it is not a period.
 */
export function formatPeriod(period: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
}
